// @flow

import React from 'react';
import { connect } from 'react-redux';
import type { Nav } from '../../../types/apiRoute';
import Link from '../../link';
import {
  getTouchMenuTitleText,
  getTouchMenuBackButtonText,
} from '../../../store/siteSettings/selectors';
import { getActiveItem, type ActiveItem } from '../../../store/routes';

type OwnProps = {|
  nav: Nav,
  isTouch?: boolean,
|};

type StateProps = {|
  activeItem: ActiveItem,
  touchMenuTitleText: string,
  touchMenuBackButtonText: string,
|};

type Props = {|
  ...OwnProps,
  ...StateProps,
|};

const activeItems = {
  item: {},
  parentItem: {},
  rootItem: {},
};

const checkIsActiveItem = ({ url }) => url === activeItems.item.url;

const checkHasActiveChild = ({ url }) => url === activeItems.parentItem.url
  || url === activeItems.rootItem.url;

const closeMenu = () => {
  const menuElement = document.getElementById('menu');
  if (menuElement) {
    if (document.body !== null) {
      document.body.style.overflow = 'visible';
    }
    menuElement.classList.remove('toggled');
  }
};

const createLink = (
  item,
  isTouch,
  useArrow = true,
  useHref = true,
) => (
  <Link
    href={useHref && item.url ? item.url : ''}
    className={`
      link
      ${checkIsActiveItem(item) ? ' active' : ''}
      ${checkHasActiveChild(item) ? ' has-active-child' : ''}
    `}
  >
    <div className="link-label">
      {isTouch && item.icon && item.icon.url
        && <img
          alt="icon"
          className="link-icon"
          width="30"
          height="30"
          src={item.icon.url}
          aria-label="icon"
        />}
      <span className="link-title">{item.title}</span>
    </div>
    {useArrow && item.children && <span className="arrow" />}
  </Link>
);

const createTouchElements = (
  level,
  parent,
  touchMenuTitleText,
  touchMenuBackButtonText,
) => (
  <>
    <li className="header">
      {level === 0
        && <h2 className="title">{touchMenuTitleText}</h2>}
      {level > 0 && (
        <a role="button" tabIndex="0" className="back-link">
          <span className="arrow" />
          <span className="label">{touchMenuBackButtonText}</span>
        </a>
      )}
      <button type="button" className="close-button" onClick={closeMenu}>
        <i className="icon-cross" />
      </button>
    </li>
    {parent && level !== 0 && (
      <li className="parent-label">
        {createLink(parent, true, false, true)}
      </li>
    )}
  </>
);

const generateLevel = (
  items,
  level,
  isTouch,
  parent,
  touchMenuTitleText,
  touchMenuBackButtonText,
) => (
  <ul className={`level-${level}${level !== 0 ? ' has-parent' : ''}`}>
    {isTouch && createTouchElements(
      level,
      parent,
      touchMenuTitleText,
      touchMenuBackButtonText,
    )}
    {items.map(item => (
      <li
        key={item.id}
        className={`${item.children ? 'has-children' : ''}`}
      >
        {createLink(
          item,
          isTouch,
          true,
          !isTouch || (isTouch && !item.children),
        )}
        {item.children && generateLevel(
          item.children,
          level + 1,
          isTouch,
          item,
          touchMenuTitleText,
          touchMenuBackButtonText,
        )}
      </li>
    ))}
  </ul>
);

const getActiveItems = (
  items,
  activeItem,
  parentItem,
  rootItem,
) => {
  items.some(item => {
    if (item.url === activeItem) {
      activeItems.item = item;
      if (parentItem) {
        activeItems.parentItem = parentItem;
      }
      activeItems.rootItem = rootItem || {};
      return true;
    }
    if (item.children) {
      getActiveItems(
        item.children,
        activeItem,
        item,
        parentItem ? rootItem : item,
      );
    }
    return false;
  });
};

const Elements = ({
  nav: { items }, isTouch, activeItem, touchMenuTitleText, touchMenuBackButtonText,
}: Props) => {
  getActiveItems(items, activeItem);
  return generateLevel(
    items,
    0,
    isTouch,
    false,
    touchMenuTitleText,
    touchMenuBackButtonText,
  );
};

Elements.defaultProps = {
  isTouch: false,
};

const mapStateToProps = (state): StateProps => ({
  activeItem: getActiveItem(state),
  touchMenuTitleText: getTouchMenuTitleText(state),
  touchMenuBackButtonText: getTouchMenuBackButtonText(state),
});

export default connect<any, OwnProps, StateProps, any, any, any>(
  mapStateToProps,
)(Elements);
